import React from "react";
import { formatBRL } from "../InvoiceSplitPreviewModal";
import {
  displayCivilDate,
  displayWeeklyPeriod,
  hasFinalInvoiceStatus,
  isWrittenOffWeek,
} from "../../../routes/pages/org.utils";
import type { WeeklyPeriod } from "../../../routes/pages/org.utils";
import type { WeeklyBilling, WeeklyStatementSummary } from "../../../routes/pages/org.weekly";
import "./WeeklyPixSummary.css";

export type WeeklyPixSummaryProps = {
  statement: WeeklyStatementSummary;
  period?: WeeklyPeriod;
  invoiceStatus?: string;
  billing?: WeeklyBilling;
};

/**
 * One read-only summary for the weekly parent, regardless of service volume.
 * @story layout padded
 * @story args { statement: { serviceAmountCents: 1014, totalAmountCents: 1014, groupCount: 2 }, period: { startsOn: "2026-10-05", endsOn: "2026-10-12", dueOn: "2026-10-14" }, invoiceStatus: "weekly-open" }
 * @story state Paid { invoiceStatus: "paid" }
 * @story state MissingPeriod { period: undefined }
 * @story state WrittenOff { invoiceStatus: "weekly-written-off", statement: { serviceAmountCents: 1021, processingFeeAmountCents: 350, totalAmountCents: 1371, groupCount: 3 }, billing: { policyVersion: 2, minimumNetServicesCents: 100, state: "written_off", disposition: "accumulation", ownServiceAmountCents: 1021, carriedServiceAmountCents: 0, netServiceAmountCents: 1021 } }
 */
export function WeeklyPixSummary({
  statement,
  period,
  invoiceStatus,
  billing,
}: WeeklyPixSummaryProps) {
  const writtenOff = isWrittenOffWeek({ invoiceStatus, weeklyBilling: billing });
  const final = writtenOff || hasFinalInvoiceStatus(invoiceStatus);
  // A forgiven week is never collected: its calendar due date is no notice.
  const due =
    writtenOff || billing?.disposition === "contribution"
      ? undefined
      : billing
        ? final
          ? period?.dueOn
          : billing.expectedChargeOn
        : period?.dueOn;
  return (
    <span className="weeklyPixSummary" data-testid="weekly-pix-summary">
      <strong data-testid="weekly-pix-summary-total">
        {formatBRL(statement.totalAmountCents)}
      </strong>
      {billing?.disposition === "accumulation" &&
        statement.processingFeeAmountCents === undefined &&
        !final && <small>Serviços registrados</small>}
      {billing && billing.carriedServiceAmountCents > 0 && (
        <small>Inclui custos de semanas anteriores</small>
      )}
      <span className="period" data-testid="weekly-pix-summary-period">
        {period ? `Semana de ${displayWeeklyPeriod(period)}` : "Período não informado"}
      </span>
      {due && (
        <small className="dueDate" data-testid="weekly-pix-summary-due-date">
          {final ? "Vencimento" : "Débito previsto"}: {displayCivilDate(due)}
        </small>
      )}
    </span>
  );
}
